/**
 * Cell-derived collision masks for catalogue buildings. The generator
 * (tools/buildgen) authors each building as a set of occupied 2 m CELLS and
 * emits them in src/edit/buildings-manifest.json; the game derives the
 * blocked-tile mask with the WALL-LINE rule on the (2*gx+1) x (2*gz+1) tile
 * lot (1 game tile = 1 metre; the mesh, centred on the lot, runs every wall
 * through the MIDDLE of a lot tile): a tile is blocked iff a shell wall
 * segment passes through it. Each boundary cell edge (occupied↔empty — the
 * same edges the generator walls) blocks the 3 tiles its 2 m segment crosses.
 * Interior tiles and outside/courtyard tiles stay open. For a plain rect this
 * is exactly the sealed perimeter ring of the lot — and it generalizes to L/U
 * shapes and the bridge street passage with no special cases. Pure — no
 * three, no DOM (consumed by bake + gen-map + the editor).
 */

export type Cell = readonly [number, number];

export interface BuildingManifestEntry {
  readonly cells: readonly Cell[];
  readonly footW: number;
  readonly footH: number;
  /** The FIRST door (back-compat readers; always === doorCells[0]). */
  readonly doorCell: { readonly x: number; readonly y: number };
  /** All doors (BV2 T2 multi-DOOR blocks). Legacy entries normalize to one. */
  readonly doorCells: readonly { readonly x: number; readonly y: number }[];
  readonly storeys: number;
  readonly enterable: boolean;
}

export type BuildingsManifest = Record<string, BuildingManifestEntry>;

/** Blocked local tile indices (ty * (2*gx+1) + tx) for an occupied-cell set. */
export function blockedTiles(cells: readonly Cell[]): Set<number> {
  const occ = new Set(cells.map(([i, j]) => `${String(i)},${String(j)}`));
  const has = (i: number, j: number): boolean => occ.has(`${String(i)},${String(j)}`);
  const gx = Math.max(...cells.map(([i]) => i)) + 1;
  const footW = 2 * gx + 1;
  const out = new Set<number>();
  const block = (tx: number, ty: number): void => {
    out.add(ty * footW + tx);
  };
  for (const [i, j] of cells) {
    if (!has(i, j - 1)) for (let d = 0; d <= 2; d++) block(2 * i + d, 2 * j); // front edge
    if (!has(i, j + 1)) for (let d = 0; d <= 2; d++) block(2 * i + d, 2 * j + 2); // back edge
    if (!has(i - 1, j)) for (let d = 0; d <= 2; d++) block(2 * i, 2 * j + d); // left edge
    if (!has(i + 1, j)) for (let d = 0; d <= 2; d++) block(2 * i + 2, 2 * j + d); // right edge
  }
  return out;
}

/** Rotate a cell set by `turns` quarter-turns clockwise (door side 0→1→2→3). */
export function rotateCellsQuarter(cells: readonly Cell[], turns: number): Cell[] {
  let cur: Cell[] = cells.map(([i, j]) => [i, j]);
  const t = ((turns % 4) + 4) % 4;
  for (let k = 0; k < t; k++) {
    const gz = Math.max(...cur.map(([, j]) => j)) + 1;
    cur = cur.map(([i, j]) => [gz - 1 - j, i]);
  }
  return cur.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
}

/**
 * Rotate a door tile position by one quarter-turn clockwise in a w×h lot.
 * One turn maps {x,y} in a w×h lot to {x: h-1-y, y: x} and swaps w/h.
 * Consistent with rotateCellsQuarter — use this to keep the door cell aligned
 * with the building when the lot is rotated (required by Task 13 placement).
 *
 * @param door - the current door tile position
 * @param w    - current lot width (tiles)
 * @param h    - current lot height (tiles)
 * @returns    - the rotated door position in the new (h × w) lot
 */
export function rotateDoorCell(
  door: { readonly x: number; readonly y: number },
  w: number,
  h: number,
): { readonly x: number; readonly y: number } {
  return { x: h - 1 - door.y, y: door.x };
}

/** Parse + validate the generated manifest JSON (throws on malformed entries). */
export function parseManifest(raw: Record<string, unknown>): BuildingsManifest {
  const out: Record<string, BuildingManifestEntry> = {};
  for (const [id, v] of Object.entries(raw)) {
    const e = v as Partial<BuildingManifestEntry>;
    if (!Array.isArray(e.cells) || typeof e.footW !== "number" || typeof e.footH !== "number") {
      throw new Error(`buildings-manifest '${id}': malformed entry`);
    }
    // Doors: a doorCells array (BV2 blocks) or the legacy singular doorCell —
    // normalized so every reader sees doorCells with >= 1 entry and
    // doorCell === doorCells[0].
    const rawDoors: readonly unknown[] =
      Array.isArray(e.doorCells) && e.doorCells.length > 0
        ? (e.doorCells as readonly unknown[])
        : e.doorCell !== undefined
          ? [e.doorCell]
          : [];
    const doorCells = rawDoors.map((raw) => {
      const d = raw as { x?: unknown; y?: unknown };
      if (typeof d.x !== "number" || typeof d.y !== "number") {
        throw new Error(`buildings-manifest '${id}': malformed door cell`);
      }
      return { x: d.x, y: d.y };
    });
    const first = doorCells[0];
    if (first === undefined) {
      throw new Error(`buildings-manifest '${id}': missing doorCell(s)`);
    }
    out[id] = {
      cells: e.cells.map((c) => {
        const cell = c as [unknown, unknown];
        return [Number(cell[0]), Number(cell[1])] as const;
      }),
      footW: e.footW,
      footH: e.footH,
      doorCell: first,
      doorCells,
      storeys: typeof e.storeys === "number" ? e.storeys : 1,
      enterable: e.enterable !== false,
    };
  }
  return out;
}
